import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { UtilityService } from '../utility.service';
import { ParentVendor } from 'app/models/ParentVendor';
import { FormSearch } from 'app/models/dashboard/FormStatus';
import { BaseDashboard } from 'app/models/dashboard/BaseDashboard';
import { environment } from 'environments/environment';
import { DuplicateVendorView } from 'app/models/form/DuplicateVendorView';
@Injectable({
	providedIn: 'root',
})
export class ParentVendorFormService {
	private readonly urlPath = 'parent-vendor';

	constructor(private readonly httpClient: HttpClient, private readonly utilityService: UtilityService) {}
	createForm(clientId: string, request: any): Observable<ParentVendor> {
		return this.httpClient
			.post<ParentVendor>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}`, this.setupRequest(request))
			.pipe();
	}
	private setupRequest(request: any) {
		const formData: FormData = new FormData();
		formData.append('model', JSON.stringify(request));
		for (let i = 0; i < request.attachments.length; i++) {
			const name = `additional file -${i + 1}`;
			formData.append(name, request.attachments[i].file);
			request.attachments[i].name = name;
		}
		return formData;
	}

	updateForm(clientId: string, formId: string, request: any): Observable<any> {
		return this.httpClient
			.put<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}`, this.setupRequest(request))
			.pipe();
	}

	removeDocument(clientId: string, formId: string, documentId: string): Observable<any> {
		return this.httpClient
			.delete<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/document/${documentId}`)
			.pipe();
	}

	getForm(id: string, clientId: string): Observable<ParentVendor> {
		return this.httpClient
			.get<ParentVendor>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${id}`)
			.pipe();
	}

	getFormsFor(clientId: string, userId: string, search: FormSearch): Observable<BaseDashboard<any>> {
		const url = `${environment.apiUrl}forms/clients/${clientId}/${
			this.urlPath
		}/users/${userId}?${this.utilityService.objectToQueryString(search)}`;
		return this.httpClient.get<BaseDashboard<any>>(url).pipe();
	}

	rejectForm(
		clientId: string,
		formId: string,
		rejectionReason: { reason: string; workflowId: number }
	): Observable<ParentVendor> {
		return this.httpClient
			.post<ParentVendor>(
				`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/decline`,
				rejectionReason
			)
			.pipe();
	}

	approveForm(clientId: string, formId: string, userId: string): Observable<ParentVendor> {
		return this.httpClient
			.post<ParentVendor>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/approve`, {
				userId,
			})
			.pipe();
	}

	sendBack(clientId: string, formId: string): Observable<ParentVendor> {
		return this.httpClient
			.post<ParentVendor>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/review`, {})
			.pipe();
	}

	exportToPdf(clientId: string, formId: string): any {
		return this.httpClient
			.get(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/export`, {
				responseType: 'blob',
			})
			.pipe();
	}

	getParentVendors(clientId: string, filter: string): Observable<ParentVendor[]> {
		return this.httpClient
			.get<ParentVendor[]>(
				`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/GetAllParentVendors/${filter}`
			)
			.pipe();
	}

	exportVendors(clientId: string, filter: string) {
		return this.httpClient
			.get(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/export/${filter}`, {
				responseType: 'blob',
			})
			.pipe();
	}

	deleteForm(clientId: string, id: string) {
		return this.httpClient.delete(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${id}`).pipe();
	}

	submitForApproval(clientId: string, id: any, userId: string): Observable<any> {
		return this.httpClient
			.post<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${id}/approval`, {
				userId,
			})
			.pipe();
	}

	uploadFile(clientId: string, id, file: File): Observable<HttpEvent<any>> {
		const formData: FormData = new FormData();
		formData.append('file', file);
		const req = new HttpRequest(
			'POST',
			`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${id}/docs`,
			formData,
			{
				reportProgress: true,
				responseType: 'json',
			}
		);
		return this.httpClient.request(req);
	}
	importVendors(clientId: string, data: any): Observable<HttpEvent<any>> {
		return this.httpClient
			.post<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/import-vendor`, data)
			.pipe();
	}
	checkDuplicateVendors(
		clientId: string,
		params: { id: string; socialSecurityNumber: string; employerIdentificationNumber: string }[]
	): Observable<DuplicateVendorView[]> {
		return this.httpClient
			.post<DuplicateVendorView[]>(
				`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/check-duplicates-parent-vendors`,
				params
			)
			.pipe();
	}
}
